import React, { useMemo, useState } from 'react';
import { LayoutChangeEvent, Modal, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Divider, IconButton, Tooltip } from 'react-native-paper';
import ChartExportDialog from './ChartExportDialog';
import { runExport } from './ChartExport';
import ChartStyles from './chartStyles';
import PieChart from './victory/PieChart';
import PieChartExportable from './victory/PieChartExportable';

export interface PieSlice {
  name: string;
  value: number;
  color: string;
}

interface PieChartCompProps {
  data: PieSlice[] | null | undefined;
  colorPallate?: any;
  ChartTitle: string;
  subTitle?: string;
}

const fsStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  card: {
    backgroundColor: '#F9FCFF',
    borderRadius: 16,
    width: '100%',
    maxWidth: 920,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  headerIcon: {
    backgroundColor: '#102B27',
    borderRadius: 8,
    height: 44,
    width: 44,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  headerTitles: {
    flex: 1,
  },
  headerTitle: {
    color: '#102B27',
    fontWeight: 'bold',
    fontSize: 16,
  },
  headerSubTitle: {
    color: '#102B27',
    fontWeight: 'bold',
    fontSize: 12,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  chartArea: {
    padding: 16,
    minHeight: 480,
    justifyContent: 'center',
  },
});

export default function PieChartComp(props: PieChartCompProps) {
  const [showFull, setShowFull] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [fsChartWidth, setFsChartWidth] = useState(0);
  const [downloadOpen, setDownloadOpen] = useState(false);

  const onChartLayout = (event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  };

  const safeBase = (props.ChartTitle || 'Chart').replace(/[\\/:*?"<>|]+/g, '-').trim();
  const exportBaseId = `${safeBase}-pie`;
  const screenRootId = `${exportBaseId}-screen`;
  const exportRootId = `${exportBaseId}-export`;

  const pieData = useMemo(
    () =>
      (props.data ?? []).map((item) => ({
        name: item.name,
        value: Math.round(Number(item.value) || 0),
        color: item.color,
      })),
    [props.data]
  );

  const filteredPie = useMemo(
    () => pieData.filter((x) => x.name != null && x.name !== 'null'),
    [pieData]
  );

  const pieExportRows = useMemo(
    () => filteredPie.map(({ name, value }) => ({ name, value })),
    [filteredPie]
  );

  const isEmpty = !props.data || props.data.length === 0;

  return (
    <View>
      <View style={ChartStyles.style}>
        {isEmpty ? (
          <View>
            <Text style={ChartStyles.chartTitle}>{`There is no data available to show ${props.ChartTitle}`}</Text>
          </View>
        ) : (
          <View>
            <View style={[ChartStyles.container, Platform.OS === 'web' ? ChartStyles.box2 : null]} onLayout={onChartLayout}>
              <View style={{ alignItems: 'center', flexDirection: 'row', width: containerWidth ?? undefined, justifyContent: 'center' }}>
                <TouchableOpacity style={ChartStyles.TitleIconHolder}>
                  <IconButton icon="chart-pie" iconColor="#ffffff" size={30} />
                </TouchableOpacity>
                <View style={ChartStyles.chartTitleContainer}>
                  <Text style={ChartStyles.chartTitle}>{props.ChartTitle}</Text>
                  {props.subTitle ? <Text style={ChartStyles.chartSubTitle}>{props.subTitle}</Text> : null}
                </View>
                {Platform.OS === 'web' && (
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <View style={ChartStyles.fullScreenIconContainer}>
                      <Tooltip title="Download">
                        <IconButton
                          icon="download"
                          iconColor={ChartStyles.chartTitle.color}
                          size={20}
                          onPress={() => setDownloadOpen(true)}
                        />
                      </Tooltip>
                    </View>
                    <View style={ChartStyles.fullScreenIconContainer}>
                      <Tooltip title="View in Full Screen">
                        <IconButton
                          icon="fullscreen"
                          iconColor={ChartStyles.chartTitle.color}
                          size={20}
                          onPress={() => setShowFull(true)}
                        />
                      </Tooltip>
                    </View>
                  </View>
                )}
              </View>
              <Divider />
              <View
                style={{ height: 360, justifyContent: 'flex-start' }}
                {...(Platform.OS === 'web' ? { id: screenRootId } : {})}
              >
                <PieChart
                  input={filteredPie}
                  donutRadius={50}
                  containerWidth={containerWidth ?? undefined}
                  legendFontColor={ChartStyles.chartTitle.color}
                />
              </View>
              {Platform.OS === 'web' && (
                <View
                  id={exportRootId}
                  style={{ position: 'absolute', left: -100000, top: 0, opacity: 0, overflow: 'visible' }}
                >
                  <PieChartExportable
                    input={filteredPie}
                    donutRadius={50}
                    chartId={exportRootId}
                    width={600}
                    mode="export"
                    height={420}
                  />
                </View>
              )}
            </View>
            <ChartExportDialog
              open={downloadOpen}
              title={`Download: ${props.ChartTitle}`}
              hasSupportingData={filteredPie.length > 0}
              exportRows={pieExportRows}
              onClose={() => setDownloadOpen(false)}
              onConfirm={async (sel) => {
                setDownloadOpen(false);
                await runExport(sel, {
                  baseName: safeBase,
                  exportSvgId: `${exportRootId}-svg`,
                  exportRows: pieExportRows,
                });
              }}
            />
          </View>
        )}
      </View>

      {showFull && (
        <Modal animationType="fade" transparent visible={showFull} onRequestClose={() => setShowFull(false)}>
          <View style={fsStyles.overlay}>
            <View style={fsStyles.card}>
              {/* Header */}
              <View style={fsStyles.header}>
                <View style={fsStyles.headerIcon}>
                  <IconButton icon="chart-pie" iconColor="#ffffff" size={22} />
                </View>
                <View style={fsStyles.headerTitles}>
                  <Text style={fsStyles.headerTitle}>{props.ChartTitle}</Text>
                  {props.subTitle ? <Text style={fsStyles.headerSubTitle}>{props.subTitle}</Text> : null}
                </View>
                <View style={fsStyles.headerActions}>
                  <Tooltip title="Download">
                    <IconButton
                      icon="download"
                      iconColor={ChartStyles.chartTitle.color}
                      size={20}
                      onPress={() => setDownloadOpen(true)}
                    />
                  </Tooltip>
                  <Tooltip title="Close">
                    <IconButton
                      icon="close"
                      iconColor={ChartStyles.chartTitle.color}
                      size={24}
                      onPress={() => setShowFull(false)}
                    />
                  </Tooltip>
                </View>
              </View>
              <Divider />
              {/* Chart area — measures its own width before rendering */}
              <View
                style={fsStyles.chartArea}
                onLayout={(e) => setFsChartWidth(e.nativeEvent.layout.width - 32)}
              >
                {fsChartWidth > 0 && (
                  <PieChartExportable
                    input={filteredPie}
                    donutRadius={50}
                    chartId={`${exportBaseId}-fs`}
                    width={fsChartWidth}
                    height={480}
                    mode="screen"
                    layout="sideLegend"
                  />
                )}
              </View>
            </View>
          </View>
        </Modal>
      )}
    </View>
  );
}
